import { Injectable } from "@angular/core";

/** Wall-clock limit for bootstrap auto-sync before {@link Bootstrap.bootstrap} stops awaiting it. */
export const BOOTSTRAP_AUTO_SYNC_FETCH_TIMEOUT_MS = 5 * 60 * 1000;

/**
 * Bounds the wall-clock time {@link GlossSyncService} spends awaiting bootstrap auto-sync so a
 * stalled HTTP call cannot block {@link Bootstrap.bootstrap}'s finally block indefinitely.
 *
 * The bound is applied at the operation level via {@link runWithTimeout} — it does NOT attach an
 * abort signal to {@link DataRepositoryService.fetch}. An earlier design aborted the shared fetch
 * funnel on timeout, which also cancelled unrelated in-flight requests during the bootstrap window;
 * racing the operation against a resolving timeout unblocks the caller without touching any request.
 * The trade-off is that the abandoned bootstrap sync is left to settle on its own rather than being
 * force-aborted (matching pre-timeout behavior).
 */
@Injectable({
  providedIn: "root",
})
export class BootstrapSyncFetchContext {
  private timeoutId: number | null = null;

  /**
   * Resolves with the operation's result, or `undefined` if it does not settle within `timeoutMs`.
   * The operation is not cancelled on timeout — it continues in the background.
   */
  async runWithTimeout<T>(
    operation: () => Promise<T>,
    timeoutMs = BOOTSTRAP_AUTO_SYNC_FETCH_TIMEOUT_MS,
  ): Promise<T | undefined> {
    try {
      return await Promise.race([
        operation(),
        new Promise<undefined>((resolve) => {
          this.timeoutId = window.setTimeout(() => resolve(undefined), timeoutMs);
        }),
      ]);
    } finally {
      this.clearTimer();
    }
  }

  /** Cancels a pending timeout (e.g. on logout). Any in-flight operation is left to settle on its own. */
  reset(): void {
    this.clearTimer();
  }

  private clearTimer(): void {
    if (this.timeoutId !== null) {
      window.clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
  }
}
